/**
 * Hierarchy Diagram View - Interactive graph visualization with React Flow
 *
 * FERP Principles:
 * - Data-driven: All data from hierarchy prop
 * - No hardcoded business logic
 * - Complete type safety
 * - Drag-and-drop with validation
 *
 * GitHub Issue: #340
 */

import React, { useMemo, useCallback, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Connection,
  Panel,
  MiniMap,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Stack,
  Group,
  Badge,
  Text,
  Box,
  Select,
  Button,
  Alert,
} from '@mantine/core';
import {
  IconUser,
  IconShield,
  IconAlertTriangle,
  IconRefresh,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import {
  createHierarchyRepository,
  type RoleHierarchy,
  type RoleNode,
  RelationType,
} from '../../data/repositories/HierarchyRepository';

export interface HierarchyDiagramViewProps {
  hierarchy: RoleHierarchy;
  tenantId: string;
  currentUser: string;
  onHierarchyChange: () => void;
}

// Custom node component
function CustomRoleNode({ data }: { data: any }) {
  const role: RoleNode = data.role;
  const hasIssues = data.hasIssues;

  const getCategoryColor = (category: string): string => {
    switch (category.toLowerCase()) {
      case 'admin':
        return '#ff6b6b';
      case 'editor':
        return '#339af0';
      case 'viewer':
        return '#51cf66';
      default:
        return '#868e96';
    }
  };

  return (
    <Box
      style={{
        padding: '12px',
        borderRadius: '8px',
        border: `2px solid ${getCategoryColor(role.category)}`,
        backgroundColor: 'white',
        minWidth: '200px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Stack gap="xs">
        {/* Header */}
        <Group justify="space-between">
          <Badge size="sm" color={role.category.toLowerCase()}>
            {role.category}
          </Badge>
          {hasIssues && (
            <IconAlertTriangle size={16} color="red" />
          )}
        </Group>

        {/* Role name */}
        <Box>
          <Text fw={700} size="sm">
            {role.displayName}
          </Text>
          <Text size="xs" c="dimmed">
            {role.roleName}
          </Text>
        </Box>

        {/* Stats */}
        <Group gap="xs">
          <Badge
            size="xs"
            variant="light"
            leftSection={<IconUser size={10} />}
          >
            {role.userCount}
          </Badge>
          <Badge
            size="xs"
            variant="light"
            leftSection={<IconShield size={10} />}
          >
            {role.permissionCount}
          </Badge>
        </Group>

        {/* Disabled indicator */}
        {!role.enabled && (
          <Badge size="xs" color="gray">
            Disabled
          </Badge>
        )}
      </Stack>
    </Box>
  );
}

const nodeTypes = {
  custom: CustomRoleNode,
};

export function HierarchyDiagramView({
  hierarchy,
  tenantId,
  currentUser,
  onHierarchyChange,
}: HierarchyDiagramViewProps) {
  const repository = useMemo(
    () => createHierarchyRepository(),
    []
  );

  const [layout, setLayout] = useState<'hierarchical' | 'radial' | 'force'>(
    'hierarchical'
  );
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Calculate node positions based on layout
  const calculatePosition = useCallback(
    (role: RoleNode, allRoles: RoleNode[]): { x: number; y: number } => {
      if (layout === 'hierarchical') {
        // Hierarchical layout: top-down based on depth
        const depth = role.depth;
        const nodesAtDepth = allRoles.filter((r) => r.depth === depth);
        const indexAtDepth = nodesAtDepth.findIndex(
          (r) => r.roleId === role.roleId
        );
        const totalAtDepth = nodesAtDepth.length;

        return {
          x: (indexAtDepth - totalAtDepth / 2) * 300,
          y: depth * 150,
        };
      } else if (layout === 'radial') {
        // Radial layout: concentric circles based on depth
        const depth = role.depth;
        const nodesAtDepth = allRoles.filter((r) => r.depth === depth);
        const indexAtDepth = nodesAtDepth.findIndex(
          (r) => r.roleId === role.roleId
        );
        const totalAtDepth = nodesAtDepth.length;
        const angle = (indexAtDepth / totalAtDepth) * 2 * Math.PI;
        const radius = depth === 0 ? 0 : 200 + depth * 150;

        return {
          x: radius * Math.cos(angle),
          y: radius * Math.sin(angle),
        };
      } else {
        // Force-directed: simple grid for now (would use d3-force in production)
        const index = allRoles.findIndex((r) => r.roleId === role.roleId);
        const cols = Math.ceil(Math.sqrt(allRoles.length));
        const row = Math.floor(index / cols);
        const col = index % cols;

        return {
          x: col * 300,
          y: row * 200,
        };
      }
    },
    [layout]
  );

  // Convert hierarchy to React Flow nodes
  const initialNodes: Node[] = useMemo(
    () =>
      hierarchy.roles.map((role) => ({
        id: role.roleId,
        type: 'custom',
        position: calculatePosition(role, hierarchy.roles),
        data: {
          role,
          hasIssues: hierarchy.validationIssues.some(
            (i) => i.roleId === role.roleId
          ),
        },
      })),
    [hierarchy, calculatePosition]
  );

  // Convert relationships to React Flow edges
  const initialEdges: Edge[] = useMemo(
    () =>
      hierarchy.relationships.map((rel) => {
        const isCircular = rel.relationshipType === RelationType.Circular;
        const isOrphaned = rel.relationshipType === RelationType.Orphaned;

        return {
          id: `${rel.fromRoleId}-${rel.toRoleId}`,
          source: rel.fromRoleId,
          target: rel.toRoleId,
          type: isCircular ? 'smoothstep' : 'default',
          animated: isCircular,
          style: {
            stroke: isCircular ? 'red' : isOrphaned ? 'orange' : '#b1b1b7',
            strokeWidth: 2,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isCircular ? 'red' : isOrphaned ? 'orange' : '#b1b1b7',
          },
        };
      }),
    [hierarchy.relationships]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes when layout changes
  React.useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        const role = hierarchy.roles.find((r) => r.roleId === node.id);
        if (!role) return node;

        return {
          ...node,
          position: calculatePosition(role, hierarchy.roles),
        };
      })
    );
  }, [layout, hierarchy.roles, calculatePosition, setNodes]);

  // Handle drag-and-drop parent change (new connection)
  const onConnect = useCallback(
    async (connection: Connection) => {
      if (!connection.source || !connection.target) return;

      try {
        setUpdateError(null);
        setUpdateSuccess(false);

        // Validate before updating
        const validation = await repository.validateParentUpdate(
          tenantId,
          connection.source,
          connection.target
        );

        if (!validation.valid) {
          setUpdateError(
            validation.error || 'Cannot create this relationship'
          );
          return;
        }

        // Update parent
        await repository.updateParent({
          tenantId,
          roleId: connection.source,
          newParentId: connection.target,
          updatedBy: currentUser,
        });

        setUpdateSuccess(true);
        setTimeout(() => setUpdateSuccess(false), 3000);

        // Reload hierarchy
        onHierarchyChange();
      } catch (err) {
        setUpdateError(
          err instanceof Error
            ? err.message
            : 'Failed to update role parent'
        );
      }
    },
    [repository, tenantId, currentUser, onHierarchyChange]
  );

  return (
    <Stack gap="md">
      {/* Controls */}
      <Group justify="space-between">
        <Select
          label="Layout"
          data={[
            { value: 'hierarchical', label: 'Hierarchical (Top-Down)' },
            { value: 'radial', label: 'Radial (Concentric)' },
            { value: 'force', label: 'Force-Directed (Grid)' },
          ]}
          value={layout}
          onChange={(value) => setLayout(value as any)}
          style={{ width: 250 }}
        />

        <Button
          variant="light"
          leftSection={<IconRefresh size={16} />}
          onClick={onHierarchyChange}
        >
          Refresh
        </Button>
      </Group>

      {/* Success message */}
      {updateSuccess && (
        <Alert icon={<IconCheck size={16} />} color="green" variant="light">
          Role parent updated successfully!
        </Alert>
      )}

      {/* Error message */}
      {updateError && (
        <Alert
          icon={<IconX size={16} />}
          color="red"
          variant="light"
          withCloseButton
          onClose={() => setUpdateError(null)}
        >
          {updateError}
        </Alert>
      )}

      {/* React Flow Canvas */}
      <Box
        style={{
          height: 600,
          border: '1px solid #dee2e6',
          borderRadius: '8px',
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-left"
        >
          <Controls />
          <MiniMap />
          <Background />

          <Panel position="top-right">
            <Box
              style={{
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              <Stack gap="xs">
                <Text size="xs" fw={700}>
                  Instructions
                </Text>
                <Text size="xs" c="dimmed">
                  • Drag nodes to reposition
                </Text>
                <Text size="xs" c="dimmed">
                  • Connect roles to change parent
                </Text>
                <Text size="xs" c="dimmed">
                  • Zoom with mouse wheel
                </Text>
                <Text size="xs" c="dimmed">
                  • Pan by dragging canvas
                </Text>
              </Stack>
            </Box>
          </Panel>
        </ReactFlow>
      </Box>

      {/* Legend */}
      <Group gap="md">
        <Text size="sm" fw={500}>
          Edge Colors:
        </Text>
        <Group gap="xs">
          <Box
            style={{
              width: 20,
              height: 2,
              backgroundColor: '#b1b1b7',
            }}
          />
          <Text size="xs">Normal</Text>
        </Group>
        <Group gap="xs">
          <Box
            style={{
              width: 20,
              height: 2,
              backgroundColor: 'red',
            }}
          />
          <Text size="xs">Circular Dependency</Text>
        </Group>
        <Group gap="xs">
          <Box
            style={{
              width: 20,
              height: 2,
              backgroundColor: 'orange',
            }}
          />
          <Text size="xs">Orphaned</Text>
        </Group>
      </Group>
    </Stack>
  );
}
